/**
 * AtlasWaypointHudModule — where your waypoints are, in the world (2026-09-24).
 *
 * "An indicator for showing waypoints in the world and what direction the waypoint is at, for when you set a waypoint
 * on the map." ONE waypoint is FOLLOWED (lib/waypoint_hud.ts: the one you set last, or the one you clicked on a map);
 * the HUD shows:
 *
 *   · a heading strip under the wallet — an arrow turned to the waypoint relative to where you look (up = ahead), its
 *     name, the distance and the words ("ahead left", "behind"); a ✕ stops following;
 *   · a marker standing ON the waypoint when it is in front of you (a gold diamond, the name, the distance);
 *   · off screen or behind you, the marker rides the screen edge on the side of the shorter turn, pointing out;
 *   · every OTHER waypoint of yours in front of you as a faint diamond with its name (the twelve nearest);
 *   · "Arrived at …" once when you reach it (again only after you have left it by 30 m).
 *
 * Positions are ABSOLUTE (the rows' posX/posZ); the camera is in RENDER space, so a point is projected at
 * absolute + scene.position (the FP64 rebase law). Hidden in space, under the loading gate, on the placement map and
 * with no waypoints. Pointer-transparent except the strip's ✕. Lever `__waypointHud()`.
 */
import type { App, IModule } from 'enginex/core/App.js';
import * as THREE from 'three/webgpu';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { myWaypoints, readTrackChoice, writeTrackChoice, WAYPOINT_COLOR, type WaypointRow } from '../lib/waypoints.js';
import { groundYIn } from '../lib/grounding.js';
import { distanceText } from '../lib/map_structures.js';
import { resolveTracked, relativeBearing, headingOf, turnWords, edgePoint, arrivalStep } from '../lib/waypoint_hud.js';

const OTHERS_MAX = 12;
/** How high over the ground the in-world marker stands (m). */
const MARKER_LIFT_M = 2.4;
/** A first look with no rows waits this long before calling the list empty (the rows may still be on the wire). */
const FIRST_LOOK_GRACE_MS = 8000;

export class AtlasWaypointHudModule implements IModule {
  readonly name = 'AtlasWaypointHud';

  private app: App | null = null;
  private root: HTMLDivElement | null = null;
  private strip: HTMLDivElement | null = null;
  private stripArrow: HTMLSpanElement | null = null;
  private stripText: HTMLSpanElement | null = null;
  private marker: HTMLDivElement | null = null;
  private markerText: HTMLDivElement | null = null;
  private edge: HTMLDivElement | null = null;
  private edgeArrow: HTMLDivElement | null = null;
  private edgeText: HTMLDivElement | null = null;
  private readonly others: HTMLDivElement[] = [];

  private world = '';
  private firstLookAt = 0;
  private seenMax: bigint | null = null;
  private trackedId = '';
  private arrived = false;
  private readonly ground = new Map<string, { x: number; z: number; y: number; at: number }>();
  private offTrack: (() => void) | null = null;
  private readonly v = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  /** The last frame's reading (the lever). */
  private last = { shown: false, tracked: '', name: '', dist: 0, rel: 0, words: '', mode: 'hidden' as 'hidden' | 'world' | 'edge', x: 0, y: 0, others: 0, arrivals: 0 };

  init(app: App): void {
    this.app = app;
    this.build();
    this.offTrack = bus.on('atlas:waypoint-track', ({ id }) => this.follow(id)); // released in dispose (2026-09-25: it leaked)
    app.world.addSystem(makeSystem('WaypointHud', Phase.UI, 34, () => this.frame()));
    (window as unknown as Record<string, unknown>).__waypointHud = (): typeof this.last & { stored: string } => ({ ...this.last, stored: this.stored() });
  }

  private build(): void {
    const root = document.createElement('div');
    root.id = 'waypoint-hud';
    root.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:31;display:none;font:600 11px var(--ax-font)'; // over the CTRL+M overlay (30), under the windows (40+)

    const strip = document.createElement('div');
    strip.id = 'waypoint-track';
    strip.style.cssText =
      'position:absolute;left:50%;top:48px;transform:translateX(-50%);display:flex;align-items:center;gap:8px;padding:4px 6px 4px 10px;' +
      'background:color-mix(in srgb, var(--ax-bg) 82%, transparent);border:1px solid var(--ax-line2);border-radius:999px;' +
      `color:var(--ax-text);white-space:nowrap;backdrop-filter:blur(4px);box-shadow:0 2px 10px rgba(0,0,0,.35)`;
    const arrow = document.createElement('span');
    arrow.textContent = '▲';
    arrow.style.cssText = `display:inline-block;color:${WAYPOINT_COLOR};font-size:13px;transition:transform .08s linear`;
    const text = document.createElement('span');
    const close = document.createElement('button');
    close.textContent = '✕';
    close.title = 'Stop following this waypoint (click a waypoint on a map to follow it again)';
    close.style.cssText = 'pointer-events:auto;cursor:pointer;background:transparent;border:none;color:var(--ax-muted);font:600 12px var(--ax-font);padding:0 4px';
    close.onclick = (e) => { e.stopPropagation(); this.follow(null); };
    strip.append(arrow, text, close);
    root.appendChild(strip);

    const marker = document.createElement('div');
    marker.style.cssText = 'position:absolute;left:0;top:0;display:none;flex-direction:column;align-items:center;gap:2px;will-change:transform';
    const diamond = document.createElement('div');
    diamond.style.cssText = `width:13px;height:13px;transform:rotate(45deg);background:${WAYPOINT_COLOR};border:2px solid var(--ax-bg);box-shadow:0 0 10px ${WAYPOINT_COLOR}`;
    const mtext = document.createElement('div');
    mtext.style.cssText = 'padding:1px 7px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 80%, transparent);color:var(--ax-text);text-shadow:0 1px 2px #000;white-space:nowrap';
    marker.append(mtext, diamond);
    root.appendChild(marker);

    const edge = document.createElement('div');
    edge.style.cssText = 'position:absolute;left:0;top:0;display:none;align-items:center;gap:6px;will-change:transform';
    const earrow = document.createElement('div');
    earrow.textContent = '➤';
    earrow.style.cssText = `color:${WAYPOINT_COLOR};font-size:22px;line-height:1;text-shadow:0 0 6px rgba(0,0,0,.8)`;
    const etext = document.createElement('div');
    etext.style.cssText = 'padding:1px 7px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 80%, transparent);color:var(--ax-text);white-space:nowrap';
    edge.append(earrow, etext);
    root.appendChild(edge);

    document.body.appendChild(root);
    this.root = root; this.strip = strip; this.stripArrow = arrow; this.stripText = text;
    this.marker = marker; this.markerText = mtext; this.edge = edge; this.edgeArrow = earrow; this.edgeText = etext;
  }

  // The choice is cached in memory with write-through (lib/waypoints readTrackChoice, 2026-09-25): stored() ran a
  // synchronous localStorage read EVERY frame.
  private stored(): string {
    if (!this.world) return '';
    return readTrackChoice(this.world);
  }

  private save(v: string): void {
    if (!this.world) return;
    writeTrackChoice(this.world, v);
  }

  /** A new world: its own stored choice, a fresh first look (waypoints are per world). */
  private enterWorld(world: string): void {
    this.world = world; this.firstLookAt = performance.now(); this.seenMax = null; this.arrived = false;
    this.ground.clear(); // the last world's ground heights (the cache was never pruned — 2026-09-25)
  }

  /** Follow a waypoint by id, or stop (null). */
  private follow(id: string | null): void {
    if (atlas.currentWorldId === null) return;
    // A follow before this world's first frame kept the OLD world's seenMax (2026-09-25): reset as frame() does.
    const world = String(atlas.currentWorldId);
    if (world !== this.world) this.enterWorld(world);
    this.save(id ?? 'off');
    this.arrived = false;
    const w = id ? myWaypoints(atlas.conn).find((r) => String(r.waypointId) === id) : null;
    bus.emit('atlas:toast', { text: w ? `Following “${w.name}”` : 'Stopped following the waypoint — click one on a map to follow it', kind: 'info', ms: 3000 });
  }

  private hide(): void {
    if (this.root && this.root.style.display !== 'none') this.root.style.display = 'none';
    this.last.shown = false;
    this.last.mode = 'hidden';
  }

  private groundAt(w: WaypointRow): number {
    const k = String(w.waypointId);
    const now = performance.now();
    const c = this.ground.get(k);
    if (c && c.x === w.posX && c.z === w.posZ && now - c.at < 2000) return c.y;
    let y = atlas.localY;
    try { if (atlas.conn) y = groundYIn(atlas.conn, atlas.currentWorldId, w.posX, w.posZ); } catch { /* keep the player's height */ }
    if (!Number.isFinite(y)) y = atlas.localY;
    this.ground.set(k, { x: w.posX, z: w.posZ, y, at: now });
    return y;
  }

  private camera(): THREE.PerspectiveCamera | null {
    const rig = atlas.localMesh;
    return (rig?.children.find((c) => (c as { isCamera?: boolean }).isCamera) as THREE.PerspectiveCamera | undefined) ?? null;
  }

  private frame(): void {
    const conn = atlas.conn;
    if (!this.root || !conn || atlas.currentWorldId === null || atlas.loadingGate || atlas.spaceFlight || atlas.placementView) { this.hide(); return; }
    const world = String(atlas.currentWorldId);
    if (world !== this.world) this.enterWorld(world);
    const rows = myWaypoints(conn);
    let maxId: bigint | null = null;
    for (const r of rows) if (maxId === null || r.waypointId > maxId) maxId = r.waypointId;
    const stored = this.stored();
    const { row, save } = resolveTracked(rows, stored, this.seenMax);
    if (save !== stored) this.save(save);
    // THE FIRST LOOK: an empty list may be rows still on the wire — call it empty only after the grace.
    if (maxId !== null) this.seenMax = this.seenMax === null || maxId > this.seenMax ? maxId : this.seenMax;
    else if (this.seenMax === null && performance.now() - this.firstLookAt > FIRST_LOOK_GRACE_MS) this.seenMax = 0n;
    if (!row) { this.hide(); return; }

    const id = String(row.waypointId);
    if (id !== this.trackedId) { this.trackedId = id; this.arrived = false; }
    const mx = atlas.localX, mz = atlas.localZ;
    const dist = Math.hypot(row.posX - mx, row.posZ - mz);
    const arr = arrivalStep(dist, this.arrived);
    this.arrived = arr.arrived;
    if (arr.justArrived) { this.last.arrivals++; bus.emit('atlas:toast', { text: `Arrived at “${row.name}”`, kind: 'success', ms: 3500 }); }

    const cam = this.camera();
    const scene = this.app?.world.scene;
    let heading = -(atlas.localYaw ?? 0); // the rig's forward (−sin yaw, −cos yaw) as a compass heading
    if (cam) { cam.getWorldDirection(this.dir); if (Math.hypot(this.dir.x, this.dir.z) > 1e-4) heading = headingOf(this.dir.x, this.dir.z); }
    const rel = relativeBearing(heading, mx, mz, row.posX, row.posZ);
    const words = this.arrived ? 'here' : turnWords(rel);
    const distTxt = this.arrived ? 'here' : distanceText(dist);

    this.root.style.display = 'block';
    this.strip!.style.top = atlas.hoverRide || atlas.skyFlight ? '104px' : '48px';
    this.stripArrow!.style.transform = `rotate(${rel.toFixed(3)}rad)`;
    this.stripText!.textContent = `${row.name} · ${distTxt}${this.arrived ? '' : ` · ${words}`}`;

    const W = window.innerWidth, H = window.innerHeight;
    let mode: 'world' | 'edge' = 'edge';
    let sx = 0, sy = 0;
    if (cam && scene) {
      const gy = this.groundAt(row) + MARKER_LIFT_M;
      cam.updateMatrixWorld();
      this.v.set(row.posX + scene.position.x, gy + scene.position.y, row.posZ + scene.position.z).applyMatrix4(cam.matrixWorldInverse);
      const csx = this.v.x, csy = this.v.y, csz = this.v.z;
      const behind = csz > -0.1;
      if (!behind) {
        this.v.applyMatrix4(cam.projectionMatrix); // camera space → clip (applyMatrix4 divides by w)
        if (Math.abs(this.v.x) <= 0.94 && Math.abs(this.v.y) <= 0.92) { mode = 'world'; sx = (this.v.x + 1) / 2 * W; sy = (1 - this.v.y) / 2 * H; }
      }
      if (mode === 'edge') { const e = edgePoint(W, H, 60, csx, -csy, behind); sx = e.x; sy = e.y; this.edgeArrow!.style.transform = `rotate(${e.angle.toFixed(3)}rad)`; }
    } else {
      const e = edgePoint(W, H, 60, Math.sin(rel), -Math.cos(rel), Math.abs(rel) > Math.PI / 2);
      sx = e.x; sy = e.y;
      this.edgeArrow!.style.transform = `rotate(${e.angle.toFixed(3)}rad)`;
    }
    if (mode === 'world') {
      this.marker!.style.display = 'flex';
      this.marker!.style.transform = `translate(${sx.toFixed(1)}px, ${sy.toFixed(1)}px) translate(-50%, -100%)`;
      this.markerText!.textContent = `${row.name} · ${distTxt}`;
      this.edge!.style.display = 'none';
    } else {
      this.edge!.style.display = 'flex';
      // The label sits on the inner side of the arrow so it never leaves the screen.
      this.edge!.style.flexDirection = sx > W / 2 ? 'row-reverse' : 'row';
      this.edge!.style.transform = `translate(${sx.toFixed(1)}px, ${sy.toFixed(1)}px) translate(${sx > W / 2 ? '-100%' : '0'}, -50%)`;
      this.edgeText!.textContent = `${row.name} · ${distTxt}`;
      this.marker!.style.display = 'none';
    }

    // The rest of your waypoints: faint diamonds where they stand, in front of you only.
    let n = 0;
    if (cam && scene) {
      const rest = rows.filter((r) => r.waypointId !== row.waypointId)
        .map((r) => ({ r, d: Math.hypot(r.posX - mx, r.posZ - mz) })).sort((a, b) => a.d - b.d).slice(0, OTHERS_MAX);
      for (const { r, d } of rest) {
        this.v.set(r.posX + scene.position.x, this.groundAt(r) + MARKER_LIFT_M + scene.position.y, r.posZ + scene.position.z).applyMatrix4(cam.matrixWorldInverse);
        if (this.v.z > -0.1) continue;
        this.v.applyMatrix4(cam.projectionMatrix);
        if (Math.abs(this.v.x) > 1 || Math.abs(this.v.y) > 1) continue;
        const el = this.otherEl(n++);
        el.style.display = 'block';
        el.style.transform = `translate(${((this.v.x + 1) / 2 * W).toFixed(1)}px, ${((1 - this.v.y) / 2 * H).toFixed(1)}px) translate(-50%, -100%)`;
        el.textContent = `◆ ${r.name} · ${distanceText(d)}`;
      }
    }
    for (let i = n; i < this.others.length; i++) this.others[i].style.display = 'none';

    this.last = { ...this.last, shown: true, tracked: id, name: row.name, dist, rel, words, mode, x: sx, y: sy, others: n };
  }

  private otherEl(i: number): HTMLDivElement {
    let el = this.others[i];
    if (!el) {
      el = document.createElement('div');
      el.style.cssText = `position:absolute;left:0;top:0;display:none;color:${WAYPOINT_COLOR};opacity:.7;white-space:nowrap;text-shadow:0 1px 2px #000;font:600 10px var(--ax-font)`;
      this.root!.appendChild(el);
      this.others.push(el);
    }
    return el;
  }

  dispose(): void {
    this.offTrack?.(); this.offTrack = null; // the bus listener outlived the module (2026-09-25)
    this.root?.remove();
    this.root = null; // frame() hides and returns while root is null
    this.ground.clear();
  }
}
